import React from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import { Modal } from 'js/design_system/Modal';
import Badge from 'js/design_system/Badge';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { trackClick } from 'js/utils/trackClick';
import { fsEvent } from 'js/fullstory';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'sitewide-js/core_pre_webpack/routes';
import { getOverrideParams } from 'js/bulk_download/bulk_download_counts_cache';

interface Props {
  workId: number | string;
  workTitle: string;
  workThumbnailUrl?: string;
  pdfCount: number;
  priceInfo?: {
    display_price: string;
    currency: string;
    country_code?: string;
  };
  open?: boolean;
  onClose(): void;
  /** When provided, records Dismissibles show/clickthrough/dismissal events
   *  for this key. Used by R2 (transactional_first_r2) for telemetry. */
  dismissibleKey?: string;
  /** Extra fields merged into the ImpressionWatcher payload. Used by R2 to
   *  include autotest_bucket / autotest fields for telemetry. */
  impressionExtra?: Record<string, unknown>;
}

function TransactionalPricingModal({
  workId,
  workTitle,
  workThumbnailUrl,
  pdfCount,
  priceInfo,
  open = true,
  onClose,
  dismissibleKey,
  impressionExtra: impressionExtraProp,
}: Props): React.ReactElement {
  const workIdNum = Number(workId);

  React.useEffect(() => {
    if (dismissibleKey) {
      window.Aedu?.Dismissibles?.recordShow(dismissibleKey);
    }
  }, [dismissibleKey]);

  React.useEffect(() => {
    fsEvent('BDTransactionalPricing.Shown', {
      workId_int: workIdNum,
      country_code_string: priceInfo?.country_code ?? 'Unknown',
    });
  }, [workIdNum, priceInfo?.country_code]);

  const onBuyNow = () => {
    if (dismissibleKey) {
      window.Aedu?.Dismissibles?.recordClickthrough(dismissibleKey);
    }

    trackClick({
      source: 'bd_transactional_pricing_modal_buy_cta',
      entityId: workIdNum,
    });

    fsEvent('BDTransactionalPricing.CTAClicked', {
      workId_int: workIdNum,
      country_code_string: priceInfo?.country_code ?? 'Unknown',
    });

    fetchWithCsrf(
      routes.bulk_download_single_purchase_checkout_session_path(
        getOverrideParams(),
      ),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_work_id: workId,
        }),
      },
    )
      .then((res) => res.json())
      .then((data: { redirect_url: string }) => {
        window.location.assign(data.redirect_url);
      });
  };

  const onDismiss = () => {
    if (dismissibleKey) {
      window.Aedu?.Dismissibles?.recordDismissal(dismissibleKey);
    }

    trackClick({
      source: 'bd_transactional_pricing_modal_dismiss',
      entityId: workIdNum,
    });

    fsEvent('BDTransactionalPricing.Dismissed', {
      workId_int: workIdNum,
      country_code_string: priceInfo?.country_code ?? 'Unknown',
    });

    onClose();
  };

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      showHeader={false}
      containerStyle={{ maxWidth: 'min(95vw, 450px)' }}
    >
      <ImpressionWatcher
        source="bd_transactional_pricing_modal"
        entity_type={ImpressionEntity.Work}
        entity_id={workIdNum}
        extra={{
          country_code: priceInfo?.country_code,
          ...impressionExtraProp,
        }}
      >
        <CloseButton type="button" onClick={onDismiss} aria-label="Close">
          <Icon name="close" size={24} />
        </CloseButton>

        <Content>
          <Header>
            <Heading size="md" style={{ color: '#222233' }}>
              Want this PDF pack without a subscription?
            </Heading>
            <Body size="md">
              Get this PDF Pack for {priceInfo?.display_price ?? '$4.99'}. No
              subscription required.
            </Body>
          </Header>

          <IllustrationArea>
            <CardStack>
              <BackCard />
              <FrontCard>
                <CardThumbnail>
                  <ThumbnailImg
                    src={workThumbnailUrl || '/images/blank-paper.jpg'}
                    alt=""
                  />
                </CardThumbnail>
                <CardTitle>{workTitle}</CardTitle>
              </FrontCard>
              <BadgeTopLeft>
                <Badge
                  text=""
                  icon="folder"
                  iconSize={16}
                  backgroundColor="#f2f5ef"
                  color="#333e2c"
                  style={{ border: '1px solid #dee6d6' }}
                />
              </BadgeTopLeft>
              {pdfCount > 0 && (
                <BadgeBottomRight>
                  <Badge
                    text={`+${pdfCount} ${pdfCount === 1 ? 'PDF' : 'PDFs'}`}
                    backgroundColor="#f2f5ef"
                    color="#333e2c"
                    style={{ border: '1px solid #dee6d6' }}
                  />
                </BadgeBottomRight>
              )}
            </CardStack>
          </IllustrationArea>

          <Actions>
            <Button iconRight="arrow_forward" onClick={onBuyNow}>
              Buy now {priceInfo?.display_price ?? '$4.99'}
            </Button>
            <NoThanksButton type="button" onClick={onDismiss}>
              <Body size="md">No thanks</Body>
            </NoThanksButton>
          </Actions>
        </Content>
      </ImpressionWatcher>
    </StyledModal>
  );
}

export default TransactionalPricingModal;

const StyledModal = styled(Modal)`
  align-items: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: var(--text-primary, #222233);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding-bottom: 12px;

  h2 {
    margin: 0;
    color: var(--text-primary, #222233);
  }

  p {
    margin: 0;
    color: var(--text-primary, #141413);
  }
`;

const IllustrationArea = styled.div`
  width: 100%;
  height: 182px;
  background: var(--background-light-blue, #eef2f9);
  border-radius: var(--border-radius-radius-lg);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const CardStack = styled.div`
  position: relative;
  width: 242px;
  height: 156px;
`;

const BackCard = styled.div`
  position: absolute;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-grey-light, #dddde2);
  border-radius: var(--border-radius-radius-md);
  width: 185px;
  height: 96px;
  top: 22px;
  left: 30px;
  transform: rotate(-10.3deg);
`;

const FrontCard = styled.div`
  position: absolute;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-grey-light, #dddde2);
  border-radius: var(--border-radius-radius-md);
  padding: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  width: 185px;
  height: 96px;
  top: 26px;
  left: 37px;
`;

const CardThumbnail = styled.div`
  width: 50px;
  height: 72px;
  flex-shrink: 0;
  border: 1px solid var(--stroke-grey-light, #dddde2);
  overflow: hidden;
`;

const ThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-family: var(--type-font-family-sans-serif, 'DM Sans'), sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--text-primary, #141413);
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
`;

const BadgeTopLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`;

const BadgeBottomRight = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding-top: 12px;
  width: 100%;
`;

const NoThanksButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: var(--text-primary, #141413);
`;
